/**
 * FNSP Eligibility Dialog Component
 * Dialog for completing the FNSP (Family and Neighborhood Support Partnership)
 * eligibility questionnaire.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  FNSPAssessment,
  FNSPAssessmentRequest,
  DEFAULT_FNSP_ASSESSMENT,
  FNSP_QUESTIONS,
} from '../types/fnsp';
import {
  getFNSPAssessment,
  saveFNSPAssessment,
} from '../services/fnspService';
import { useServiceAreaZips } from '../hooks/useServiceAreaZips';
import { formatDate } from '../utils/dateUtils';
import '../styles/FNSPDialog.css';

interface FNSPEligibilityDialogProps {
  individualId: number;
  clientName: string;
  zipCode: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function FNSPEligibilityDialog({
  individualId,
  clientName,
  zipCode,
  isOpen,
  onClose,
  onSaved,
}: FNSPEligibilityDialogProps) {
  const [formData, setFormData] = useState<FNSPAssessmentRequest>(DEFAULT_FNSP_ASSESSMENT);
  const [existingAssessment, setExistingAssessment] = useState<FNSPAssessment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isInServiceArea: checkServiceArea } = useServiceAreaZips();

  // Load existing assessment if available
  const loadExistingAssessment = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const assessment = await getFNSPAssessment(individualId);
      if (assessment) {
        setExistingAssessment(assessment);
        // Populate form with existing values
        setFormData({
          hasChildUnder18OrDisabled21: assessment.hasChildUnder18OrDisabled21,
          affectedByPoverty: assessment.affectedByPoverty,
          homelessOrUnstable: assessment.homelessOrUnstable,
          parentIncarcerated: assessment.parentIncarcerated,
          immigrationChallenges: assessment.immigrationChallenges,
          familyConflictOrDV: assessment.familyConflictOrDV,
          childWelfareInvolved: assessment.childWelfareInvolved,
          sexualAbuseOrTrafficking: assessment.sexualAbuseOrTrafficking,
          significantDisability: assessment.significantDisability,
          lgbtq: assessment.lgbtq,
          academicFailure: assessment.academicFailure,
          excessiveAbsences: assessment.excessiveAbsences,
          schoolBehaviorProblems: assessment.schoolBehaviorProblems,
          juvenileJustice: assessment.juvenileJustice,
          mentalHealthSymptoms: assessment.mentalHealthSymptoms,
          substanceAbuse: assessment.substanceAbuse,
        });
      } else {
        setExistingAssessment(null);
        setFormData(DEFAULT_FNSP_ASSESSMENT);
      }
    } catch (err) {
      console.error('Error loading FNSP assessment:', err);
      // Don't show error for 404 - just means no assessment exists yet
      setExistingAssessment(null);
      setFormData(DEFAULT_FNSP_ASSESSMENT);
    } finally {
      setIsLoading(false);
    }
  }, [individualId]);

  useEffect(() => {
    if (isOpen && individualId) {
      loadExistingAssessment();
    }
  }, [isOpen, individualId, loadExistingAssessment]);

  const handleCheckboxChange = (field: keyof FNSPAssessmentRequest) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const countRiskFactors = (): number => {
    const riskFactors = [
      'affectedByPoverty',
      'homelessOrUnstable',
      'parentIncarcerated',
      'immigrationChallenges',
      'familyConflictOrDV',
      'childWelfareInvolved',
      'sexualAbuseOrTrafficking',
      'significantDisability',
      'lgbtq',
      'academicFailure',
      'excessiveAbsences',
      'schoolBehaviorProblems',
      'juvenileJustice',
      'mentalHealthSymptoms',
      'substanceAbuse',
    ] as (keyof FNSPAssessmentRequest)[];

    return riskFactors.filter(field => formData[field]).length;
  };

  const calculateEligibilityPreview = () => {
    const hasChild = formData.hasChildUnder18OrDisabled21;
    const riskFactorCount = countRiskFactors();
    const inServiceArea = checkServiceArea(zipCode);

    const isEligible = hasChild && riskFactorCount >= 2 && inServiceArea;

    const reasons = [];
    if (!hasChild) {reasons.push('No child under 18/21 with disability in household');}
    if (riskFactorCount < 2) {reasons.push(`Only ${riskFactorCount} risk factor(s) (minimum 2 required)`);}
    if (!inServiceArea) {reasons.push(`Zip code ${zipCode || 'unknown'} not in service area`);}

    return {
      isEligible,
      reasons,
      riskFactorCount,
      inServiceArea,
    };
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await saveFNSPAssessment(individualId, formData);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save assessment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(DEFAULT_FNSP_ASSESSMENT);
    setError(null);
    onClose();
  };

  if (!isOpen) {return null;}

  const eligibilityPreview = calculateEligibilityPreview();

  return (
    <div className="fnsp-dialog-overlay" onClick={handleCancel}>
      <div className="fnsp-dialog" onClick={e => e.stopPropagation()}>
        <div className="fnsp-dialog-header">
          <h2>FNSP Eligibility Assessment</h2>
          <p className="fnsp-dialog-subtitle">
            Family and Neighborhood Support Partnership
          </p>
          <p className="fnsp-dialog-client">
            {clientName} {zipCode && `(Zip: ${zipCode})`}
          </p>
          <button className="fnsp-dialog-close" onClick={handleCancel} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="fnsp-dialog-content">
          {isLoading ? (
            <div className="fnsp-loading">Loading assessment...</div>
          ) : (
            <>
              {error && (
                <div className="fnsp-error">{error}</div>
              )}

              {existingAssessment && (
                <div className="fnsp-existing-notice">
                  <strong>Note:</strong> An existing assessment was found (dated{' '}
                  {existingAssessment.assessmentDate
                    ? formatDate(existingAssessment.assessmentDate)
                    : 'unknown'}
                  ). You can update the answers below.
                </div>
              )}

              {/* Eligibility Preview */}
              <div className={`fnsp-eligibility-preview ${eligibilityPreview.isEligible ? 'eligible' : 'not-eligible'}`}>
                <div className="fnsp-preview-status">
                  <span className="fnsp-preview-icon">
                    {eligibilityPreview.isEligible ? '\u2713' : '\u2717'}
                  </span>
                  <span className="fnsp-preview-text">
                    {eligibilityPreview.isEligible
                      ? 'Currently ELIGIBLE based on selections'
                      : 'Currently NOT ELIGIBLE'}
                  </span>
                </div>
                <div className="fnsp-preview-details">
                  <span>Risk factors: {eligibilityPreview.riskFactorCount}/2 minimum</span>
                  <span>Service area: {eligibilityPreview.inServiceArea ? 'Yes' : 'No'}</span>
                </div>
                {!eligibilityPreview.isEligible && eligibilityPreview.reasons.length > 0 && (
                  <ul className="fnsp-preview-reasons">
                    {eligibilityPreview.reasons.map((reason, i) => (
                      <li key={i}>{reason}</li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Questions */}
              <div className="fnsp-questions">
                {FNSP_QUESTIONS.map((q) => (
                  <div
                    key={q.field}
                    className={`fnsp-question ${q.sequence === 0 ? 'fnsp-question-required' : ''}`}
                  >
                    <label className="fnsp-question-label">
                      <input
                        type="checkbox"
                        checked={formData[q.field]}
                        onChange={() => handleCheckboxChange(q.field)}
                        className="fnsp-checkbox"
                      />
                      {q.sequence !== 0 && <span className="fnsp-question-number">{q.sequence}.</span>}
                      <span className="fnsp-question-text">{q.question}</span>
                      {q.required && <span className="fnsp-required-badge">Required</span>}
                    </label>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="fnsp-dialog-footer">
          <button
            className="button-secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="button-primary"
            onClick={handleSubmit}
            disabled={isLoading || isSaving}
          >
            {isSaving ? 'Saving...' : existingAssessment ? 'Update Assessment' : 'Save Assessment'}
          </button>
        </div>
      </div>
    </div>
  );
}
